import { doubleJoin, findOwnProperty } from "./object-helper";

/**
 * Replaces indexed portions of a string with the corresponding value passed in the arguments.
 * ie: If the string was 'Text {0} text {1} text {0}', each {0} would be replaced with the second
 * argument passed in, and each {1} would be replaced with the third argument passed in, etc.
 * @param str The string to replace tokens in
 * @param args Strings to replace each occurence of {n} with
 * @returns A new string with tokens replaced
 */
export function replaceTokens(str: string, ...args: string[]) {
  let returnStr = str;
  if (str) {
    for (let i = 0; i < args.length; i += 1) {
      returnStr = returnStr.replace(new RegExp(`\\{${i}\\}`, "g"), args[i]);
    }
  }

  return returnStr;
}

/**
 * Returns URL with QS params removed
 * @param url The URL to trim
 * @returns trimmed URL
 */
export const trimQsParams = (url: string) =>
  url.includes("?") ? url.slice(0, url.indexOf("?")) : url;

/**
 * Remove HTML tags from string
 * @param html HTML content
 * @returns string
 */
export const removeHtmlTags = (html: string): string => html.replace(/<[^>]*>?/g, "");

/** map characters to HTML entities. */
const htmlEscapes: { [key: string]: string } = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

/** map HTML entities to characters. */
const htmlUnescapes: { [key: string]: string } = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
};

/** match HTML entities and HTML characters. */
const reUnescapedHtml = /[&<>"']/g;
const reHasUnescapedHtml = RegExp(reUnescapedHtml.source);
const reEscapedHtml = /&(?:amp|lt|gt|quot|#39);/g;
const reHasEscapedHtml = RegExp(reEscapedHtml.source);

/**
 * Escapes html characters in a string
 * @param html The "unsafe" string to add html escape characters to
 * @returns "safe" escaped input text
 */
export const htmlEscape = (html: string): string =>
  html && reHasUnescapedHtml.test(html)
    ? html.replace(reUnescapedHtml, (key: string) => htmlEscapes[key])
    : html;

/**
 * Unscapes html characters in a string -- returned string should be named "unsafe"
 * @param text The string to remove html escape characters from
 * @returns "unsafe" unescaped string
 */
export const htmlUnescape = (text: string): string =>
  text && reHasEscapedHtml.test(text)
    ? text.replace(reEscapedHtml, (key: string) => htmlUnescapes[key])
    : text;

/**
 * Creates a dictionary by splitting a String with two delimeters. First, splits this string using delimeter one.
  After the split, each section will be split into two parts on the first occurence of delimiterTwo, resulting in a KEY string and a VALUE string.
  If the VALUE string is empty (the section doesn't contain delimiterTwo, or the only occurence is at the end of the section),
  the value in the dictionary for that KEY will be null. Empty Keys are stored as Empty Strings, but empty values are stored as null.
  By default, if the VALUE string is at least 1 character long, the entire VALUE string will be stored as the value for that Key.
  However, if multi-valued keys are enabled (true passed as param), the VALUE string will be split using delimiterTwo, and the resulting array
  will be stored as the value for that KEY.
 * @param str The string to split
 * @param delimiterOne The delimeter sepearting each node of the dictionary
 * @param delimiterTwo The delimeter sepearting the key/value(s) of each node
 * @param multiValuedKeys If TRUE, the value for each KEY will either be null, or an array of 1 or more values,
 * the result of splitting each section by delimiterTwo (except for the 1st value, which is used as the KEY)
 * @param keyTransformFunc If defined the key will be passed to the transformer before being used in the dictionary.
 * @returns A dictionary
 */
export function doubleSplit(
  str: string,
  delimiterOne: string,
  delimiterTwo: string,
  multiValuedKeys?: boolean,
  keyTransformFunc?: Function,
): { [key: string]: null | string | Array<string> } {
  const result: { [key: string]: null | string | Array<string> } = {};

  if (str) {
    str.split(delimiterOne).forEach((item) => {
      if (item) {
        const parts = item.split(delimiterTwo);
        let key = parts[0];
        if (keyTransformFunc) {
          key = keyTransformFunc(key);
        }

        if (parts.length === 1) {
          result[key] = null;
        } else if (multiValuedKeys) {
          result[key] = parts.slice(1);
        } else {
          result[key] = parts.slice(1).join(delimiterTwo);
        }
      }
    });
  }

  return result;
}

/**
 * Removes all leading and trailing white space from the String
 * @param str The string to trim
 * @returns The trimmed string
 */
export function trim(str: string) {
  return str.replace(/^\s+|\s+$/g, "");
}

/**
 * @private
 */
export const getLinkInnerText = (innerText: string, parentheticalText: string | null) => {
  if (innerText !== parentheticalText) {
    return `${innerText} (${parentheticalText})`;
  }

  return innerText;
};

/**
 * This method is used to modify certain HTML anchor elements (links) to remove on-click behavior
 * @param text - HTML or text to filter
 * @param allowContactProtocols - Indicator that `mailto:` and `tel:` protocols for anchor elements should be filtered as well. Defaults to true.
 * @returns Modified text/HTML where anchor elements' `href` attributes are replaced with parentheticalText; Possibly doing the same for `mailto:` or `tel:` protocols.
 */
export const filterLinks = (text: string, allowContactProtocols: boolean = true): string => {
  const div = document.createElement("div");
  div.innerHTML = text;

  const links = div.getElementsByTagName("a");
  const numLinks = links.length;

  for (let idx = numLinks - 1; idx >= 0; idx -= 1) {
    const link = links[idx];
    let { innerText } = link;
    const { protocol } = link;
    let skipReplacement = false;

    if (protocol === "mailto:" || protocol === "tel:") {
      if (allowContactProtocols) {
        skipReplacement = true;
      } else {
        innerText = getLinkInnerText(innerText, link.pathname);
      }
    } else {
      innerText = getLinkInnerText(innerText, link.getAttribute("href"));
    }

    if (!skipReplacement) {
      const span = document.createElement("span");
      span.innerText = innerText;

      link.parentNode?.replaceChild(span, link);
    }
  }

  return div.innerHTML;
};

interface IParsedUrl {
  originAndPath: string;
  query: Record<string, unknown> | null;
  fragment: Record<string, unknown> | null;
}

/**
 * Parses a URL and extracts the query string and fragment converted into maps.
 * @param url The URL to parse
 * @returns The parsed URL
 */
export const parseQueryString = (url: string): IParsedUrl => {
  let originAndPath = url;
  let query = null;
  let fragment = null;

  if (url) {
    const queryStartIndex = url.indexOf("?");
    const fragmentStartIndex = url.indexOf("#");

    if (
      fragmentStartIndex !== -1 &&
      (queryStartIndex === -1 || fragmentStartIndex < queryStartIndex)
    ) {
      // The URL only has a fragment and no query
      originAndPath = url.substring(0, fragmentStartIndex);
      fragment = doubleSplit(url.substring(fragmentStartIndex + 1), "&", "=");
    } else if (queryStartIndex !== -1 && fragmentStartIndex === -1) {
      // The URL only has query and no fragment
      originAndPath = url.substring(0, queryStartIndex);
      query = doubleSplit(url.substring(queryStartIndex + 1), "&", "=");
    } else if (queryStartIndex !== -1 && fragmentStartIndex !== -1) {
      // The URL has both query and fragment
      originAndPath = url.substring(0, queryStartIndex);
      query = doubleSplit(url.substring(queryStartIndex + 1, fragmentStartIndex), "&", "=");
      fragment = doubleSplit(url.substring(fragmentStartIndex + 1), "&", "=");
    }
  }

  const parsedUrl = {
    originAndPath,
    query,
    fragment,
  };

  return parsedUrl;
};

/**
 * Joins a URL that has been previously parsed into extracted query string and fragment maps.
 * @param parsedUrl The parsed URL to join
 * @returns The joined URL
 */
export const joinUrl = (parsedUrl: IParsedUrl): string => {
  let url = parsedUrl.originAndPath || "";

  if (parsedUrl.query) {
    url += `?${doubleJoin(parsedUrl.query, "&", "=")}`;
  }

  if (parsedUrl.fragment) {
    url += `#${doubleJoin(parsedUrl.fragment, "&", "=")}`;
  }

  return url;
};

/**
 * Adds (or overwrites) the given key-value pairs to a URL's query string
 * TODO: Determine use-case for this vs `appendOrReplaceQueryStringParams`
 * @param url URL to modify
 * @param keyValuePairs The query string parameters to add
 * @returns Modified URL
 */
export const addQueryStrings = (url: string, keyValuePairs: Map<string, string>): string => {
  if (url && keyValuePairs.size) {
    const parsedUrl = parseQueryString(url);
    const query = parsedUrl.query || {};

    if (query !== null) {
      keyValuePairs.forEach((value, key) => {
        query[key] = value;
      });

      parsedUrl.query = query;
      return joinUrl(parsedUrl);
    }
  }

  return url;
};

/**
 * transfer missing query parameters to target urls
 * @param queryParamString query parameter string
 * @param targetUrlString target URL string
 * @returns target url with missing query parameters from source
 */
export const copyQueryStringParameters = (queryParamString: string, targetUrlString: string) => {
  const queryParams = new URLSearchParams(queryParamString);
  const targetUrl = new URL(targetUrlString);

  queryParams.forEach((value, name) => {
    if (!targetUrl.searchParams.has(name)) {
      targetUrl.searchParams.set(name, value);
    }
  });

  return targetUrl.href;
};

/**
 * Removes a URL's query string parameters
 * @param url The URL to modify.
 * @param params An array with the parameters to remove.
 * @returns The modified URL
 */
export const removeQueryStringParameters = (url: string, params: string[]) => {
  const parsedUrl = parseQueryString(url);
  params.forEach((param: string) => {
    parsedUrl.query = parsedUrl.query || {};
    const existingParam = findOwnProperty(parsedUrl.query, param, true);
    if (existingParam) {
      delete parsedUrl.query[existingParam];
    }
  });

  return joinUrl(parsedUrl);
};

/**
 * Gets a current query string parameter value
 * @param param The name of the query string parameter to extract
 * @param urlOrQueryString A full URL in which to search or just the query string part including "?".
 * Defaults to `window.location.search`.
 * @returns The Query String parameter value if found. Otherwise an empty string.
 */
export const extractQueryStringParam = (param: string, urlOrQueryString?: string): string => {
  let url = urlOrQueryString;
  if (!url && url !== "") {
    url = window.location.search;
  }

  const parsedUrl = parseQueryString(url);
  parsedUrl.query = parsedUrl.query || {};

  const existingParam = findOwnProperty(parsedUrl.query, param, true);
  return existingParam ? (parsedUrl.query[existingParam] as string) : "";
};

/**
 * Adds or modifies a URL's query string parameters. Note that if a record is passed in for queryString,
 * the keys will be lower-cased before being added to the url
 *
 * TODO: Determine use-case for this vs `addQueryStrings`
 *
 * @param url URL to modify
 * @param queryString Query string with params to append or replace
 * @param lowerCaseParamKeys Whether parameter keys should be lower-cased. Keys are not lower-cased by default
 * @param maxLength Maximum length of new URL allowed
 * @returns Modified URL
 */
export const appendOrReplaceQueryStringParams = (
  url: string,
  queryString: string | Record<string, unknown>,
  lowerCaseParamKeys?: boolean,
  maxLength?: number,
): string => {
  const parsedUrl = parseQueryString(url);
  const existingParams = parsedUrl.query || {};

  const newParams =
    typeof queryString === "string"
      ? // convert to a record if queryString is a string
        doubleSplit(queryString, "&", "=")
      : queryString;

  // add new params to existing params
  parsedUrl.query = Object.keys(newParams).reduce((acc: Record<string, unknown>, key: string) => {
    const finalKey = lowerCaseParamKeys ? key.toLowerCase() : key;
    acc[finalKey] = newParams[key] || null;
    return acc;
  }, existingParams);

  const modifiedUrl = joinUrl(parsedUrl);
  return maxLength && modifiedUrl.length > maxLength ? url : modifiedUrl;
};

/**
 * Returns the given URL with its query string and fragment stripped out.
 * @param url the url whose base URL is to be returned
 * @returns the base url (with the query string and fragment removed) of the input url
 */
export const stripQueryStringAndFragment = (url: string) => parseQueryString(url).originAndPath;
